import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import type { StudioDefinition } from '../../../domain';
import { AgentPanel } from './AgentPanel';
import { DEFAULT_RIGHT_RAIL_WIDTH } from '../layout/railWidths';

type ActionTab = 'operations' | 'agents';

interface Props {
  studio: StudioDefinition;
  /**
   * Operations tab content — the active op's parameter form + the chip
   * dock. Passed as children so the shell owns the operation lifecycle
   * (active kind, source, mask, etc.) without ActionPanel needing to
   * thread every prop.
   */
  operationsContent: React.ReactNode;
  /** Pixel width of the panel. Defaults to DEFAULT_RIGHT_RAIL_WIDTH; the
   *  shell passes the user-resizable value when the splitter is wired. */
  width?: number;
  /**
   * Gap 4 — when set, opening an AgentInvocationCard from the Agents tab
   * seeds its Instruction textarea with this text. Writer's Lab passes the
   * active section's synopsis (Storyteller) or other ambient context so the
   * "Run" click is one step closer to useful for the cold-start affordance
   * (interview questions, hashtag suggestions, etc.).
   */
  defaultAgentInstruction?: string;
}

/**
 * Right-rail primary action space (v3 UI spec §4.3, revised 2026-05-17).
 * Combines the operation form + dock and the agent helpers under a single
 * tabbed surface — operations and agents are both "what do I do next?"
 * controls, and keeping them in one rail keeps the user's eye in one
 * place. Center column is canvas-only after this restructure.
 */
export const ActionPanel: React.FC<Props> = ({ studio, operationsContent, width = DEFAULT_RIGHT_RAIL_WIDTH, defaultAgentInstruction }) => {
  const [tab, setTab] = useState<ActionTab>('operations');

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: theme => `1px solid ${theme.palette.divider}`,
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
      aria-label="Action panel"
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as ActionTab)}
        variant="fullWidth"
        sx={{ minHeight: 38, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
      >
        <Tab value="operations" label="Operations" sx={{ minHeight: 38, fontSize: '0.8rem' }} />
        <Tab value="agents" label="Agents" sx={{ minHeight: 38, fontSize: '0.8rem' }} />
      </Tabs>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {tab === 'operations' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
            {operationsContent}
          </Box>
        )}
        {tab === 'agents' && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <AgentPanel
              agentIds={studio.featuredAgents}
              defaultPromptAgentIds={studio.defaultPromptAgents}
              defaultInstruction={defaultAgentInstruction}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
